import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Thermometer, Truck, Store, 
  QrCode, MapPin, ShieldCheck, Leaf
} from 'lucide-react';
import { brandAssets } from '../data/mockData';

const Home = () => {
  const { language } = useLanguage();
  const t = language === 'fr';

  return (
    <div className="min-h-screen bg-[#F8F2E8] dark:bg-[#0F1A14]">
      
      {/* Hero Section - Épuré */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1A5C38] via-[#1A5C38] to-[#2D7A4E]" />
          <div className="absolute inset-0 opacity-10">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <defs>
                <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                  <circle cx="1" cy="1" r="0.5" fill="white" />
                </pattern>
              </defs>
              <rect width="100" height="100" fill="url(#grid)" />
            </svg>
          </div>
        </div>

        <div className="container relative z-10 py-16 md:py-24">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="mb-6"
            >
              <img src={brandAssets.logoWhite || brandAssets.logo} alt="Océana" className="h-16 md:h-20 mx-auto filter brightness-0 invert" />
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6"
              style={{ fontFamily: 'Syne, sans-serif' }}
            >
              {t 
                ? 'Fruits & légumes frais à Abidjan'
                : 'Fresh fruits & vegetables in Abidjan'}
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg text-white/80 mb-8 max-w-xl mx-auto"
            >
              {t
                ? 'Qualité premium, traçabilité totale, livraison fraîcheur.'
                : 'Premium quality, full traceability, freshness delivery.'}
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-wrap justify-center gap-4"
            >
              <Link 
                to="/produits"
                className="inline-flex items-center gap-2 bg-[#F5A623] hover:bg-[#E09000] text-[#1A5C38] font-semibold px-6 py-3 rounded-full transition-all"
              >
                {t ? 'Voir nos produits' : 'View products'}
                <ArrowRight size={18} />
              </Link>
              <Link 
                to="/contact"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium px-6 py-3 rounded-full transition-all border border-white/20"
              >
                {t ? 'Nous contacter' : 'Contact us'}
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Hub Abidjan - Section avec vraies images */}
      <section className="py-16 md:py-20 bg-[#1A5C38]">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#F5A623] font-medium tracking-wider uppercase text-sm mb-4">
                {t ? 'Hub d\'Abidjan' : 'Abidjan Hub'}
              </p>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
                {t 
                  ? 'La chaîne du froid jamais rompue'
                  : 'The cold chain never broken'}
              </h2>
              <p className="text-white/80 mb-8">
                {t
                  ? 'Notre hub est le cœur de notre logistique : chambre froide multi-température, mûrisserie pilotée, et flotte frigorifique dédiée.'
                  : 'Our hub is the heart of our logistics: multi-temperature cold storage, controlled ripening rooms, and dedicated refrigerated fleet.'}
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { label: t ? 'Chambre froide' : 'Cold storage', value: t ? 'Multi-température' : 'Multi-temperature' },
                  { label: t ? 'Mûrisserie' : 'Ripening rooms', value: t ? 'Pilotée' : 'Controlled' },
                  { label: t ? 'Livraison' : 'Delivery', value: t ? 'Mûri à point' : 'Ripened to perfection' },
                  { label: t ? 'Couverture' : 'Coverage', value: 'Abidjan' }
                ].map((item, i) => (
                  <div key={i} className="bg-white/10 rounded-lg p-4">
                    <p className="text-white/60 text-sm">{item.label}</p>
                    <p className="text-white font-semibold">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <img 
                src={brandAssets.hubWarehouse}
                alt="Hub Océana Abidjan"
                className="rounded-2xl shadow-2xl w-full aspect-[4/3] object-cover"
              />
              <div className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 bg-white rounded-xl p-3 md:p-4 shadow-lg">
                <p className="text-[#1A5C38] font-bold text-xl md:text-2xl">365j/an</p>
                <p className="text-gray-600 text-xs md:text-sm">{t ? 'Approvisionnement continu' : 'Continuous supply'}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SmartTrace CTA */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-[#1A5C38] to-[#2D7A4E]">
        <div className="container">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1 text-center md:text-left">
              <div className="inline-flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full text-white/80 text-sm mb-4">
                <QrCode size={16} />
                SmartTrace
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                {t ? 'Traçabilité Totale' : 'Full Traceability'}
              </h2>
              <p className="text-white/80 mb-6">
                {t 
                  ? 'Scannez le QR code de votre produit et découvrez son parcours complet : origine, récolte, transport, certifications.'
                  : 'Scan your product QR code and discover its complete journey: origin, harvest, transport, certifications.'}
              </p>
              <Link 
                to="/tracabilite"
                className="inline-flex items-center gap-2 bg-[#F5A623] hover:bg-[#E09000] text-[#1A5C38] font-semibold px-6 py-3 rounded-full transition-all"
              >
                {t ? 'Vérifier un produit' : 'Verify a product'}
                <ArrowRight size={18} />
              </Link>
            </div>
            <div className="flex-shrink-0">
              <div className="w-40 h-40 bg-white rounded-2xl flex items-center justify-center shadow-xl">
                <QrCode className="text-[#1A5C38]" size={80} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Diallo Network CTA */}
      <section className="py-16 md:py-20 bg-[#F8F2E8] dark:bg-[#0F1A14]">
        <div className="container">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row-reverse items-center gap-8">
            <div className="flex-1 text-center md:text-left">
              <div className="inline-flex items-center gap-2 bg-[#1A5C38]/10 dark:bg-[#1A5C38]/30 px-3 py-1 rounded-full text-[#1A5C38] dark:text-[#2D8A56] text-sm mb-4">
                <Store size={16} />
                {t ? 'Réseau de Proximité' : 'Neighborhood Network'}
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A5C38] dark:text-white mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                {t ? 'Boutiques Diallo' : 'Diallo Stores'}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t 
                  ? '10 boutiques pilotes à Abidjan. Fruits frais Océana à deux pas de chez vous, avec programme fidélité et stock en temps réel.'
                  : '10 pilot stores in Abidjan. Fresh Océana fruits just steps from your home, with loyalty program and real-time stock.'}
              </p>
              <Link 
                to="/diallo"
                className="inline-flex items-center gap-2 bg-[#1A5C38] hover:bg-[#145230] text-white font-semibold px-6 py-3 rounded-full transition-all"
              >
                {t ? 'Trouver une boutique' : 'Find a store'}
                <MapPin size={18} />
              </Link>
            </div>
            <div className="flex-shrink-0">
              <div className="w-40 h-40 bg-[#1A5C38] rounded-2xl flex items-center justify-center shadow-xl">
                <Store className="text-white" size={60} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Simple Stats */}
      <section className="py-12 bg-white dark:bg-[#1A2B22]">
        <div className="container">
          <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto">
            {[
              { value: '10+', label: t ? 'Années' : 'Years' },
              { value: '50+', label: t ? 'Produits' : 'Products' },
              { value: '100%', label: t ? 'Traçable' : 'Traceable' }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-[#1A5C38] dark:text-[#F5A623]">{stat.value}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GMS Partners - Credibility */}
      <section className="py-8 bg-[#F8F2E8] dark:bg-[#0F1A14]">
        <div className="container">
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-4">
            {t ? 'Ils nous font confiance' : 'They trust us'}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12 opacity-60">
            {['Carrefour', 'Prosuma', 'CDCI', 'Hôtels 5★'].map((name, i) => (
              <span key={i} className="text-[#1A5C38] dark:text-gray-400 font-medium text-sm">
                {name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Eco Badge */}
      <section className="py-8 bg-[#F8F2E8] dark:bg-[#0F1A14]">
        <div className="container">
          <div className="flex items-center justify-center gap-3 text-[#1A5C38] dark:text-[#2D8A56]">
            <Leaf size={20} />
            <span className="text-sm font-medium">
              {t ? 'Engagés pour une agriculture durable' : 'Committed to sustainable agriculture'}
            </span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
